/**
 * Keyword matching for the defect-history picker.
 *
 * Searches recently completed events on the same tail for defects whose text
 * overlaps the defect being written up, so a controller can see how the same
 * discrepancy was corrected before. Pure logic, kept out of the rendering module
 * so it can be reasoned about and tested on its own.
 */
import { MaintenanceDefect, UnscheduledEvent } from '../../types';
import { DEFECT_HISTORY_LOOKBACK_DAYS } from '../../constants';

/** A related defect found on an earlier completed event for the same tail. */
export interface HistoricalMatch {
  eventId: string;
  erts: string;
  defectText: string;
  correctiveAction: string;
}

/**
 * Words that carry no diagnostic signal, excluded from keyword matching so
 * common filler text cannot produce a match on its own.
 */
const DEFECT_KEYWORD_STOP_WORDS: ReadonlySet<string> = new Set<string>([
  'the',
  'and',
  'for',
  'was',
  'are',
  'with',
  'that',
  'this',
  'from',
  'not',
  'during',
  'after',
  'before',
  'between',
  'above',
  'below',
  'left',
  'right',
  'per',
  'found',
  'noted',
  'indication',
  'indications',
  'caution',
  'warning',
]);

/** Keywords must be longer than this to be considered diagnostic. */
const DEFECT_KEYWORD_MIN_LENGTH = 2;
/** A historical defect must share at least this many keywords to be a match. */
const DEFECT_HISTORY_MIN_KEYWORD_MATCHES = 2;
/** Record types that carry defects worth searching for related history. */
const DEFECT_HISTORY_RECORD_TYPES: readonly string[] = ['Unscheduled', 'Planned', 'Deferred'];
/** Only closed events are searched, since they carry a corrective action. */
const SEARCHABLE_HISTORY_STATUS = 'COMPLETED';

/** Reduces defect text to the lowercase keywords used for history matching. */
function extractDefectKeywords(defectText: string): string[] {
  return defectText
    .toLowerCase()
    .split(/\s+/)
    .filter(
      (word: string): boolean =>
        word.length > DEFECT_KEYWORD_MIN_LENGTH && !DEFECT_KEYWORD_STOP_WORDS.has(word),
    );
}

/** The date portion of an event's ERTS, or `''` when it has none. */
function historyErtsDate(event: UnscheduledEvent): string {
  if (!event.returnToServiceTimeZulu) return '';
  return (
    event.returnToServiceTimeZulu.split(' ')[0] || event.returnToServiceTimeZulu.split('T')[0] || ''
  );
}

/**
 * True when a completed event is eligible to be searched for related defects on
 * the given tail: matching tail, closed, a defect-bearing record type, and not
 * older than the lookback window. An event with no parseable ERTS is kept.
 */
function isSearchableHistoryEvent(
  event: UnscheduledEvent,
  tailNumber: string,
  earliestErtsMs: number,
): boolean {
  if (event.tailNumber !== tailNumber || event.status !== SEARCHABLE_HISTORY_STATUS) return false;
  if (!event.recordType || !DEFECT_HISTORY_RECORD_TYPES.includes(event.recordType)) return false;
  const ertsDate = historyErtsDate(event);
  if (!ertsDate) return true;
  const parsedErts = new Date(ertsDate);
  return isNaN(parsedErts.getTime()) || parsedErts.getTime() >= earliestErtsMs;
}

/** Collects an event's defects whose text shares enough keywords with the query. */
function collectEventMatches(
  event: UnscheduledEvent,
  keywords: string[],
  minimumMatchCount: number,
): HistoricalMatch[] {
  const ertsDate = historyErtsDate(event);
  // Defect text is authoritative in the defects array (from defect-log).
  return (event.defects || [])
    .filter((defect: MaintenanceDefect): boolean => {
      const defectTextLower = defect.text.toLowerCase();
      const matchCount = keywords.filter((word: string): boolean =>
        defectTextLower.includes(word),
      ).length;
      return matchCount >= minimumMatchCount;
    })
    .map(
      (defect: MaintenanceDefect): HistoricalMatch => ({
        eventId: event.eventId,
        erts: ertsDate,
        defectText: defect.text,
        correctiveAction: event.correctiveAction || '',
      }),
    );
}

/**
 * Searches completed events for defects similar to `defectText` on the same
 * tail. A match needs at least two shared keywords (or one, when the text yields
 * only one). Returns matches in event order, then defect order.
 */
export function findRelatedDefectHistory(
  defectText: string,
  completedEvents: UnscheduledEvent[] | undefined,
  tailNumber: string | undefined,
): HistoricalMatch[] {
  if (!completedEvents || !tailNumber || !defectText.trim()) return [];
  const keywords: string[] = extractDefectKeywords(defectText);
  if (keywords.length === 0) return [];
  const earliestErtsMs: number = Date.now() - DEFECT_HISTORY_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
  const minimumMatchCount: number = Math.min(DEFECT_HISTORY_MIN_KEYWORD_MATCHES, keywords.length);
  const matches: HistoricalMatch[] = [];
  for (const candidate of completedEvents) {
    if (!isSearchableHistoryEvent(candidate, tailNumber, earliestErtsMs)) continue;
    matches.push(...collectEventMatches(candidate, keywords, minimumMatchCount));
  }
  return matches;
}
